import { useMemo, useState } from 'react';
import {
  Sparkles,
  Rocket,
  Bot,
  Code,
  Code2,
  Cpu,
  Globe,
  Bug,
  Wrench,
  Zap,
  Shield,
  Database,
  Server,
  Terminal,
  Box,
  Workflow,
  GitBranch,
  Bell,
  Search,
  FileText,
  Hammer,
  Brain,
  Wand2,
  Flame,
  Leaf,
  Star,
  Heart,
  Eye,
  Compass,
  Briefcase,
  Beaker,
  Scale,
  PenTool,
  Megaphone,
  Headphones,
  Lock,
  X,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import { Input } from '@reflex/ui/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@reflex/ui/components/ui/popover';
import { cn } from '@reflex/ui/lib/utils';

/**
 * Curated list of Lucide icons appropriate for agent personas. Each entry
 * includes search keywords so users can find icons by intent ("debug",
 * "ship", "review", etc.) rather than just the icon name.
 */
interface IconEntry {
  name: string;
  Icon: LucideIcon;
  keywords: readonly string[];
}

const ICONS: readonly IconEntry[] = [
  { name: 'sparkles', Icon: Sparkles, keywords: ['ai', 'magic', 'shine', 'special'] },
  { name: 'rocket', Icon: Rocket, keywords: ['ship', 'launch', 'deploy', 'fast'] },
  { name: 'bot', Icon: Bot, keywords: ['robot', 'agent', 'ai', 'automation'] },
  { name: 'brain', Icon: Brain, keywords: ['ai', 'think', 'mind', 'intelligence'] },
  { name: 'wand', Icon: Wand2, keywords: ['magic', 'transform', 'wizard'] },
  { name: 'zap', Icon: Zap, keywords: ['lightning', 'fast', 'power', 'energy'] },
  { name: 'flame', Icon: Flame, keywords: ['fire', 'hot', 'trending'] },
  { name: 'star', Icon: Star, keywords: ['favorite', 'best', 'top'] },
  { name: 'heart', Icon: Heart, keywords: ['love', 'like', 'favorite'] },
  { name: 'eye', Icon: Eye, keywords: ['watch', 'monitor', 'observe', 'review'] },
  { name: 'compass', Icon: Compass, keywords: ['navigate', 'direction', 'explore'] },
  { name: 'code', Icon: Code, keywords: ['develop', 'programming', 'engineer'] },
  { name: 'code2', Icon: Code2, keywords: ['develop', 'programming', 'engineer'] },
  { name: 'terminal', Icon: Terminal, keywords: ['shell', 'cli', 'command', 'console'] },
  { name: 'cpu', Icon: Cpu, keywords: ['processor', 'compute', 'hardware', 'model'] },
  { name: 'database', Icon: Database, keywords: ['data', 'storage', 'sql', 'db'] },
  { name: 'server', Icon: Server, keywords: ['backend', 'infrastructure', 'host'] },
  { name: 'box', Icon: Box, keywords: ['package', 'blueprint', 'container'] },
  { name: 'workflow', Icon: Workflow, keywords: ['flow', 'pipeline', 'process'] },
  { name: 'git-branch', Icon: GitBranch, keywords: ['git', 'branch', 'fork', 'version'] },
  { name: 'globe', Icon: Globe, keywords: ['web', 'internet', 'world', 'network'] },
  { name: 'bug', Icon: Bug, keywords: ['debug', 'fix', 'issue', 'error'] },
  { name: 'wrench', Icon: Wrench, keywords: ['tool', 'fix', 'repair', 'maintenance'] },
  { name: 'hammer', Icon: Hammer, keywords: ['build', 'construct', 'tool'] },
  { name: 'shield', Icon: Shield, keywords: ['security', 'protect', 'safe'] },
  { name: 'lock', Icon: Lock, keywords: ['security', 'private', 'secure'] },
  { name: 'bell', Icon: Bell, keywords: ['notify', 'alert', 'reminder'] },
  { name: 'search', Icon: Search, keywords: ['find', 'lookup', 'query'] },
  { name: 'file-text', Icon: FileText, keywords: ['document', 'text', 'content', 'docs'] },
  { name: 'pen-tool', Icon: PenTool, keywords: ['design', 'write', 'edit'] },
  { name: 'beaker', Icon: Beaker, keywords: ['lab', 'experiment', 'test', 'science'] },
  { name: 'scale', Icon: Scale, keywords: ['balance', 'compare', 'judge', 'review'] },
  { name: 'briefcase', Icon: Briefcase, keywords: ['work', 'business', 'manager'] },
  { name: 'megaphone', Icon: Megaphone, keywords: ['announce', 'marketing', 'broadcast'] },
  { name: 'headphones', Icon: Headphones, keywords: ['support', 'listen', 'audio'] },
  { name: 'leaf', Icon: Leaf, keywords: ['green', 'eco', 'fresh', 'nature'] },
] as const;

const ICON_BY_NAME = new Map(ICONS.map((e) => [e.name, e]));

/**
 * Curated emoji set with searchable aliases. Hardcoded (rather than pulling
 * in a multi-megabyte emoji DB) since the picker only needs a useful subset
 * for naming agent personas.
 */
interface EmojiEntry {
  char: string;
  keywords: readonly string[];
}

const EMOJIS: readonly EmojiEntry[] = [
  { char: '🚀', keywords: ['rocket', 'ship', 'launch', 'deploy', 'fast'] },
  { char: '🤖', keywords: ['robot', 'bot', 'agent', 'ai'] },
  { char: '🧠', keywords: ['brain', 'think', 'smart', 'ai'] },
  { char: '✨', keywords: ['sparkles', 'magic', 'special', 'ai'] },
  { char: '⚡', keywords: ['zap', 'lightning', 'fast', 'energy'] },
  { char: '🔥', keywords: ['fire', 'hot', 'flame', 'trending'] },
  { char: '⭐', keywords: ['star', 'favorite', 'top'] },
  { char: '💎', keywords: ['gem', 'diamond', 'premium', 'value'] },
  { char: '🎯', keywords: ['target', 'goal', 'aim', 'focus'] },
  { char: '🛠️', keywords: ['tools', 'build', 'fix', 'repair'] },
  { char: '🔧', keywords: ['wrench', 'tool', 'fix', 'config'] },
  { char: '🔨', keywords: ['hammer', 'build', 'construct'] },
  { char: '🐛', keywords: ['bug', 'debug', 'fix', 'issue'] },
  { char: '🔍', keywords: ['search', 'find', 'review', 'inspect'] },
  { char: '🧪', keywords: ['test', 'lab', 'experiment', 'science'] },
  { char: '📦', keywords: ['package', 'box', 'release', 'ship'] },
  { char: '📊', keywords: ['chart', 'analytics', 'metrics', 'data'] },
  { char: '📈', keywords: ['growth', 'metrics', 'increase', 'trending'] },
  { char: '📝', keywords: ['note', 'write', 'document', 'edit'] },
  { char: '📚', keywords: ['docs', 'documentation', 'books', 'reference'] },
  { char: '💬', keywords: ['chat', 'talk', 'message', 'comment'] },
  { char: '💡', keywords: ['idea', 'lightbulb', 'insight', 'think'] },
  { char: '🎨', keywords: ['art', 'design', 'paint', 'creative'] },
  { char: '🎭', keywords: ['theater', 'mask', 'persona', 'role'] },
  { char: '🦾', keywords: ['arm', 'strong', 'mechanical', 'cyborg'] },
  { char: '👀', keywords: ['eyes', 'watch', 'review', 'observe'] },
  { char: '🛡️', keywords: ['shield', 'security', 'protect', 'safe'] },
  { char: '🔐', keywords: ['lock', 'security', 'private'] },
  { char: '🌍', keywords: ['globe', 'world', 'web', 'international'] },
  { char: '🌱', keywords: ['plant', 'seedling', 'growth', 'new'] },
  { char: '🌟', keywords: ['glowing', 'star', 'special'] },
  { char: '⚙️', keywords: ['gear', 'settings', 'config', 'engine'] },
  { char: '🧩', keywords: ['puzzle', 'piece', 'integration', 'plugin'] },
  { char: '🪄', keywords: ['wand', 'magic', 'transform', 'wizard'] },
  { char: '🎓', keywords: ['graduate', 'learn', 'school', 'expert'] },
  { char: '👨‍💻', keywords: ['developer', 'engineer', 'programmer'] },
  { char: '👩‍💻', keywords: ['developer', 'engineer', 'programmer'] },
  { char: '🦉', keywords: ['owl', 'wise', 'review', 'night'] },
  { char: '🐙', keywords: ['octopus', 'multitask', 'github'] },
  { char: '🦄', keywords: ['unicorn', 'rare', 'special'] },
  { char: '🎪', keywords: ['circus', 'show', 'event'] },
  { char: '📡', keywords: ['antenna', 'broadcast', 'signal', 'receive'] },
  { char: '🛸', keywords: ['ufo', 'space', 'alien'] },
  { char: '🧭', keywords: ['compass', 'navigate', 'direction'] },
  { char: '🪐', keywords: ['planet', 'space', 'saturn'] },
  { char: '☕', keywords: ['coffee', 'cafe', 'morning'] },
  { char: '🍕', keywords: ['pizza', 'food', 'fun'] },
  { char: '🎮', keywords: ['gaming', 'game', 'play', 'controller'] },
] as const;

/**
 * Resolve a stored icon string into a renderable element. Returns `null` when
 * the value is missing or unrecognised so callers can fall back to initials
 * or the default avatar.
 */
export function renderPersonaIcon(
  icon: string | null | undefined,
  className: string,
): React.ReactNode | null {
  if (!icon) return null;
  if (icon.startsWith('lucide:')) {
    const entry = ICON_BY_NAME.get(icon.slice('lucide:'.length));
    if (!entry) return null;
    const Icon = entry.Icon;
    return <Icon className={className} aria-hidden />;
  }
  return (
    <span className={cn('leading-none', className)} aria-hidden>
      {icon}
    </span>
  );
}

/**
 * Read-only preview of the currently selected icon — used inside the picker
 * trigger so the parent form doesn't have to duplicate the rendering rules.
 */
export function PersonaIconPreview({
  icon,
  fallback,
  className,
}: {
  icon: string | null | undefined;
  fallback: React.ReactNode;
  className?: string;
}) {
  const rendered = renderPersonaIcon(icon, className ?? 'h-5 w-5');
  return <>{rendered ?? fallback}</>;
}

interface PersonaIconPickerProps {
  /** Currently selected icon (null = no custom icon). */
  value: string | null;
  /** Called when the user picks a new icon, or `null` to clear back to default. */
  onChange: (next: string | null) => void;
  /** Trigger element. Receives the current value via `value` prop on the parent. */
  trigger: React.ReactNode;
}

export function PersonaIconPicker({ value, onChange, trigger }: PersonaIconPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filteredIcons = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ICONS;
    return ICONS.filter((e) => e.name.includes(q) || e.keywords.some((k) => k.includes(q)));
  }, [query]);

  const filteredEmojis = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return EMOJIS;
    return EMOJIS.filter((e) => e.keywords.some((k) => k.includes(q)));
  }, [query]);

  function selectIcon(name: string) {
    onChange(`lucide:${name}`);
    setOpen(false);
  }
  function selectEmoji(char: string) {
    onChange(char);
    setOpen(false);
  }
  function clear() {
    onChange(null);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        className="w-80 overflow-hidden p-0"
        align="start"
        onOpenAutoFocus={(e) => {
          // Let the search input grab focus instead of the icon grid.
          e.preventDefault();
        }}
      >
        <div className="flex items-center gap-2 border-b border-border/60 px-2.5 py-2">
          <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search icons or emoji…"
            aria-label="Search icons or emoji"
            className="h-7 border-0 bg-transparent px-0 text-xs shadow-none focus-visible:ring-0"
          />
          {value && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
              onClick={clear}
              aria-label="Clear icon"
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>

        {filteredIcons.length === 0 && filteredEmojis.length === 0 ? (
          <EmptyResult />
        ) : (
          <div className="max-h-80 overflow-y-auto overscroll-contain p-2">
            <div className="grid grid-cols-7 gap-1">
              {filteredIcons.map(({ name, Icon }) => {
                const selected = value === `lucide:${name}`;
                return (
                  <button
                    key={`icon:${name}`}
                    type="button"
                    title={name}
                    aria-label={`Use ${name} icon`}
                    onClick={() => selectIcon(name)}
                    className={cn(
                      'flex h-9 w-9 items-center justify-center rounded-md transition-colors',
                      'hover:bg-muted',
                      selected && 'bg-primary/15 text-primary ring-1 ring-primary/40',
                    )}
                  >
                    <Icon className="h-4 w-4" aria-hidden />
                  </button>
                );
              })}
              {filteredEmojis.map(({ char, keywords }) => {
                const selected = value === char;
                return (
                  <button
                    key={`emoji:${char}`}
                    type="button"
                    title={keywords[0]}
                    aria-label={`Use ${keywords[0]} emoji`}
                    onClick={() => selectEmoji(char)}
                    className={cn(
                      'flex h-9 w-9 items-center justify-center rounded-md text-lg leading-none transition-colors',
                      'hover:bg-muted',
                      selected && 'bg-primary/15 ring-1 ring-primary/40',
                    )}
                  >
                    {char}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function EmptyResult() {
  return (
    <div className="flex h-32 items-center justify-center px-4 text-center text-xs text-muted-foreground">
      No matches. Try a different keyword.
    </div>
  );
}
